import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Calendar, Eye, ArrowRight, FileText, Video, Download, File, Image, ExternalLink, Play, Heart, Share2, Facebook, Twitter, Linkedin, Link2, MessageCircle, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicationsPage = () => {
  const { t, language } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [likedArticles, setLikedArticles] = useState(() => {
    const saved = localStorage.getItem('oar_liked_articles');
    return saved ? JSON.parse(saved) : [];
  });
  const [shareMenuOpen, setShareMenuOpen] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const INITIAL_DISPLAY_COUNT = 4;

  // Handle like
  const handleLike = async (articleId) => {
    const isLiked = likedArticles.includes(articleId);
    try {
      await axios.post(`${API}/articles/${articleId}/like`, { unlike: isLiked });
      
      let newLikedArticles;
      if (isLiked) {
        newLikedArticles = likedArticles.filter(id => id !== articleId);
      } else {
        newLikedArticles = [...likedArticles, articleId];
      }
      
      setLikedArticles(newLikedArticles);
      localStorage.setItem('oar_liked_articles', JSON.stringify(newLikedArticles));
      
      // Update article likes count locally
      setArticles(prev => prev.map(a => 
        a.id === articleId 
          ? { ...a, likes: (a.likes || 0) + (isLiked ? -1 : 1) }
          : a
      ));
      
      if (!isLiked) {
        toast.success('Merci pour votre like !');
      }
    } catch (error) {
      console.error('Error liking article:', error);
    }
  };

  // Share functions
  const getShareUrl = (article) => {
    return `${window.location.origin}/publications#${article.slug}`;
  };

  const shareOnFacebook = (article) => {
    const url = encodeURIComponent(getShareUrl(article));
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
  };

  const shareOnTwitter = (article) => {
    const url = encodeURIComponent(getShareUrl(article));
    const text = encodeURIComponent(getTitle(article));
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank', 'width=600,height=400');
  };

  const shareOnLinkedIn = (article) => {
    const url = encodeURIComponent(getShareUrl(article));
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'width=600,height=400');
  };

  const shareOnWhatsApp = (article) => {
    const url = encodeURIComponent(getShareUrl(article));
    const text = encodeURIComponent(`${getTitle(article)} - ${getExcerpt(article)}`);
    window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
  };

  const copyLink = async (article) => {
    try {
      await navigator.clipboard.writeText(getShareUrl(article));
      setCopiedLink(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API}/articles`);
        // Filter only published articles
        const publishedArticles = response.data.filter(a => a.is_published !== false);
        setArticles(publishedArticles);
      } catch (error) {
        console.error('Error fetching articles:', error);
        setArticles([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const categories = [
    { key: 'all', label: t('publications.categories.all') || 'Tous' },
    { key: 'article', label: 'Articles' },
    { key: 'annonce', label: 'Annonces' },
    { key: 'rapport', label: t('publications.categories.rapport') || 'Rapports' },
    { key: 'note_strategique', label: t('publications.categories.note_strategique') || 'Notes Stratégiques' },
    { key: 'analyse_geopolitique', label: t('publications.categories.analyse_geopolitique') || 'Analyses' },
  ];

  const types = [
    { key: 'all', label: 'Tous les types', icon: FileText },
    { key: 'article', label: 'Articles', icon: FileText },
    { key: 'annonce', label: 'Annonces', icon: FileText },
    { key: 'video', label: 'Vidéos', icon: Video },
  ];

  const filteredArticles = articles.filter(a => {
    const categoryMatch = selectedCategory === 'all' || a.category === selectedCategory;
    const typeMatch = selectedType === 'all' || a.publication_type === selectedType;
    return categoryMatch && typeMatch;
  });

  // Display only first 4 articles unless "showAll" is true
  const displayedArticles = showAll ? filteredArticles : filteredArticles.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMoreArticles = filteredArticles.length > INITIAL_DISPLAY_COUNT;

  const getTitle = (article) => {
    return article[`title_${language}`] || article.title_fr || article.title_en;
  };

  const getExcerpt = (article) => {
    return article[`excerpt_${language}`] || article.excerpt_fr || article.excerpt_en;
  };

  const getContent = (article) => {
    return article[`content_${language}`] || article.content_fr || article.content_en;
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.key === category);
    return cat ? cat.label : category;
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'video': return Video;
      case 'annonce': return FileText;
      default: return FileText;
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'video': return 'Vidéo';
      case 'annonce': return 'Annonce';
      default: return 'Article';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'ru' ? 'ru-RU' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : url;
  };

  const getAttachmentIcon = (type) => {
    switch(type) {
      case 'pdf': return <FileText className="w-4 h-4 text-red-500" />;
      case 'image': return <Image className="w-4 h-4 text-green-500" />;
      default: return <File className="w-4 h-4 text-blue-500" />;
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-32 md:py-40 bg-gradient-to-br from-oar-blue via-oar-blue-dark to-oar-blue">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <span className="text-xs md:text-sm uppercase tracking-[0.2em] text-oar-orange font-bold">
            Think Tank
          </span>
          <h1
            data-testid="publications-title"
            className="font-heading text-4xl md:text-6xl font-bold tracking-tight text-white mt-4 mb-6"
          >
            {t('publications.title') || 'Publications'}
          </h1>
          <p className="max-w-2xl text-lg text-white/80">
            {t('publications.subtitle') || 'Découvrez nos analyses, rapports et actualités sur la coopération Afrique-Russie'}
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-6 bg-white border-b border-oar-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Type Filter */}
          <div className="flex flex-wrap gap-2 mb-4">
            {types.map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.key}
                  onClick={() => setSelectedType(type.key)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-all ${
                    selectedType === type.key
                      ? 'bg-oar-blue text-white'
                      : 'text-oar-text-secondary hover:bg-oar-bg border border-oar-border'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {type.label}
                </button>
              );
            })}
          </div>
          
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setSelectedCategory(cat.key)}
                data-testid={`filter-${cat.key}`}
                className={`px-3 py-1.5 text-xs rounded transition-all ${
                  selectedCategory === cat.key
                    ? 'bg-oar-orange text-white'
                    : 'text-oar-text-muted hover:text-oar-orange border border-oar-border hover:border-oar-orange'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 bg-oar-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {isLoading ? (
            <div className="text-center text-oar-text-secondary py-16">
              <div className="animate-spin w-8 h-8 border-2 border-oar-blue border-t-transparent rounded-full mx-auto mb-4" />
              Chargement...
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 text-oar-text-muted mx-auto mb-4" />
              <p className="text-oar-text-secondary text-lg">
                {t('publications.noArticles') || 'Aucune publication disponible'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6">
                {displayedArticles.map((article) => {
                const TypeIcon = getTypeIcon(article.publication_type);
                const isExpanded = expandedArticle === article.id;
                
                return (
                  <article
                    key={article.id}
                    data-testid={`article-${article.slug}`}
                    className="bg-white border border-oar-border rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all group"
                  >
                    {/* Image or Video Thumbnail */}
                    {article.video_url ? (
                      <div className="relative h-48 bg-gray-900">
                        <iframe
                          src={getYouTubeEmbedUrl(article.video_url)}
                          title={getTitle(article)}
                          className="w-full h-full"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                        <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 bg-purple-600 text-white text-xs rounded">
                          <Video className="w-3 h-3" />
                          Vidéo
                        </div>
                      </div>
                    ) : article.image_url ? (
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={article.image_url}
                          alt={getTitle(article)}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-3 left-3 flex items-center gap-2">
                          <span className={`flex items-center gap-1 px-2 py-1 text-xs rounded ${
                            article.publication_type === 'video' ? 'bg-purple-600 text-white' :
                            article.publication_type === 'annonce' ? 'bg-oar-orange text-white' :
                            'bg-oar-blue text-white'
                          }`}>
                            <TypeIcon className="w-3 h-3" />
                            {getTypeLabel(article.publication_type)}
                          </span>
                          <span className="px-2 py-1 bg-white/90 text-oar-text text-xs rounded">
                            {getCategoryLabel(article.category)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="h-32 bg-gradient-to-br from-oar-blue to-oar-blue-dark flex items-center justify-center">
                        <TypeIcon className="w-12 h-12 text-white/50" />
                        <div className="absolute top-3 left-3 flex items-center gap-2">
                          <span className={`flex items-center gap-1 px-2 py-1 text-xs rounded ${
                            article.publication_type === 'annonce' ? 'bg-oar-orange text-white' :
                            'bg-white/20 text-white'
                          }`}>
                            {getTypeLabel(article.publication_type)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Content */}
                    <div className="p-5">
                      <h2 className="font-heading text-lg text-oar-text font-semibold mb-2 line-clamp-2 group-hover:text-oar-blue transition-colors">
                        {getTitle(article)}
                      </h2>
                      <p className="text-oar-text-secondary text-sm leading-relaxed mb-4 line-clamp-3">
                        {getExcerpt(article)}
                      </p>

                      {/* Attachments */}
                      {article.attachments && article.attachments.length > 0 && (
                        <div className="mb-4 p-3 bg-oar-bg rounded-lg">
                          <p className="text-xs font-medium text-oar-text-secondary mb-2 flex items-center gap-1">
                            <Download className="w-3 h-3" />
                            Fichiers à télécharger ({article.attachments.length})
                          </p>
                          <div className="space-y-1">
                            {article.attachments.slice(0, isExpanded ? undefined : 2).map((att, idx) => (
                              <a
                                key={idx}
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-xs text-oar-blue hover:text-oar-blue-dark hover:underline"
                              >
                                {getAttachmentIcon(att.type)}
                                <span className="truncate">{att.name}</span>
                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                              </a>
                            ))}
                            {article.attachments.length > 2 && !isExpanded && (
                              <button
                                onClick={() => setExpandedArticle(article.id)}
                                className="text-xs text-oar-orange hover:underline"
                              >
                                + {article.attachments.length - 2} autres fichiers
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Meta */}
                      <div className="flex items-center justify-between text-xs text-oar-text-muted border-t border-oar-border pt-3">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(article.published_at)}
                          </span>
                          {article.views > 0 && (
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {article.views}
                            </span>
                          )}
                        </div>
                        <span className="text-oar-blue font-medium truncate max-w-[100px]" title={article.author}>
                          {article.author}
                        </span>
                      </div>

                      {/* Like and Share buttons */}
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-oar-border">
                        {/* Like Button */}
                        <button
                          onClick={() => handleLike(article.id)}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all ${
                            likedArticles.includes(article.id)
                              ? 'bg-red-50 text-red-500'
                              : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-500'
                          }`}
                          data-testid={`like-btn-${article.id}`}
                        >
                          <Heart 
                            className={`w-4 h-4 ${likedArticles.includes(article.id) ? 'fill-current' : ''}`} 
                          />
                          <span className="text-sm font-medium">{article.likes || 0}</span>
                        </button>

                        {/* Share Button */}
                        <div className="relative">
                          <button
                            onClick={() => setShareMenuOpen(shareMenuOpen === article.id ? null : article.id)}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 hover:bg-oar-blue-soft hover:text-oar-blue transition-all"
                            data-testid={`share-btn-${article.id}`}
                          >
                            <Share2 className="w-4 h-4" />
                            <span className="text-sm font-medium">Partager</span>
                          </button>

                          {/* Share Menu */}
                          {shareMenuOpen === article.id && (
                            <div className="absolute right-0 bottom-full mb-2 bg-white rounded-lg shadow-lg border border-oar-border p-2 z-20 min-w-[200px]">
                              <p className="text-xs text-oar-text-muted px-2 py-1 mb-1">Partager sur :</p>
                              <button
                                onClick={() => shareOnFacebook(article)}
                                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded transition-all"
                              >
                                <Facebook className="w-4 h-4" />
                                Facebook
                              </button>
                              <button
                                onClick={() => shareOnTwitter(article)}
                                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-500 rounded transition-all"
                              >
                                <Twitter className="w-4 h-4" />
                                X (Twitter)
                              </button>
                              <button
                                onClick={() => shareOnLinkedIn(article)}
                                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded transition-all"
                              >
                                <Linkedin className="w-4 h-4" />
                                LinkedIn
                              </button>
                              <button
                                onClick={() => shareOnWhatsApp(article)}
                                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 rounded transition-all"
                              >
                                <MessageCircle className="w-4 h-4" />
                                WhatsApp
                              </button>
                              <div className="border-t border-oar-border my-1"></div>
                              <button
                                onClick={() => copyLink(article)}
                                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-all"
                              >
                                {copiedLink ? <Check className="w-4 h-4 text-green-500" /> : <Link2 className="w-4 h-4" />}
                                {copiedLink ? 'Copié !' : 'Copier le lien'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Read More / Expand */}
                      {(getContent(article) || article.video_url) && (
                        <div className="mt-4">
                          <button
                            onClick={() => setExpandedArticle(isExpanded ? null : article.id)}
                            className="inline-flex items-center gap-2 text-oar-orange text-sm font-medium hover:gap-3 transition-all"
                          >
                            <FileText className="w-4 h-4" />
                            {isExpanded ? 'Réduire' : (t('publications.readMore') || 'Lire plus')}
                            <ArrowRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          </button>
                        </div>
                      )}

                      {/* Expanded Content */}
                      {isExpanded && getContent(article) && (
                        <div className="mt-4 pt-4 border-t border-oar-border">
                          <div className="text-sm text-oar-text-secondary leading-relaxed whitespace-pre-wrap">
                            {getContent(article)}
                          </div>
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
              </div>
              
              {/* Show More / Show Less Button */}
              {hasMoreArticles && (
                <div className="text-center mt-10">
                  <button
                    onClick={() => setShowAll(!showAll)}
                    className="inline-flex items-center gap-2 px-8 py-3 bg-oar-blue text-white rounded-lg hover:bg-oar-blue-dark transition-all font-medium"
                    data-testid="show-more-publications"
                  >
                    {showAll ? (
                      <>
                        Afficher moins
                        <ArrowRight className="w-4 h-4 rotate-[-90deg]" />
                      </>
                    ) : (
                      <>
                        Voir toutes les publications ({filteredArticles.length})
                        <ArrowRight className="w-4 h-4 rotate-90" />
                      </>
                    )}
                  </button>
                </div>
              )}
              
              {/* Articles count info */}
              <p className="text-center text-sm text-oar-text-muted mt-4">
                {showAll 
                  ? `${filteredArticles.length} publication(s) affichée(s)`
                  : `${displayedArticles.length} sur ${filteredArticles.length} publication(s)`
                }
              </p>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default PublicationsPage;
